import { z } from 'zod';

export const mailProviderKindSchema = z.enum([
  'generic_imap_smtp',
  'gmail_oauth',
  'microsoft_oauth',
  'custom_oauth2',
]);
export type MailProviderKind = z.infer<typeof mailProviderKindSchema>;

export const mailProviderIdSchema = z.enum([
  'gmail_oauth',
  'microsoft_oauth',
  'gmx',
  'web_de',
  'mailbox_org',
  'posteo',
  'ionos',
  'mail_de',
  'freenet',
  'strato',
  'generic_imap_smtp',
  'custom_oauth2',
]);
export type MailProviderId = z.infer<typeof mailProviderIdSchema>;

export const mailWatchStrategySchema = z.enum([
  'gmail_pubsub',
  'microsoft_graph',
  'jmap_push',
  'imap_idle',
  'reconciliation_only',
]);
export type MailWatchStrategy = z.infer<typeof mailWatchStrategySchema>;

export const mailWatchStatusSchema = z.enum([
  'inactive',
  'activating',
  'active',
  'renewing',
  'degraded',
  'permission_blocked',
]);
export type MailWatchStatus = z.infer<typeof mailWatchStatusSchema>;

export const mailSignalIdentitySchema = z.discriminatedUnion('source', [
  z.object({
    source: z.literal('gmail'),
    messageId: z.string().min(1),
    historyId: z.string().min(1),
  }),
  z.object({
    source: z.literal('microsoft_graph'),
    notificationId: z.string().min(1),
    subscriptionId: z.string().min(1),
    resource: z.string().min(1),
    changeType: z.string().min(1),
  }),
  z.object({
    source: z.literal('microsoft_graph_capture'),
    captureId: z.string().uuid(),
    payloadSha256: z.string().regex(/^[a-f0-9]{64}$/),
  }),
  z.object({
    source: z.literal('jmap'),
    subscriptionId: z.string().min(1),
    accountId: z.string().min(1),
    type: z.string().min(1),
    states: z.record(z.string(), z.string()),
  }),
  z.object({
    source: z.literal('imap'),
    leaseId: z.string().min(1),
    eventOrdinal: z.number().int().nonnegative(),
    uidValidity: z.string().min(1),
    uidNext: z.string().min(1),
  }),
  z.object({
    source: z.literal('manual'),
    requestId: z.string().uuid(),
  }),
]);
export type MailSignalIdentity = z.infer<typeof mailSignalIdentitySchema>;

export const mailChangeSignalV1Schema = z.object({
  schemaVersion: z.literal(1),
  tenantId: z.string().min(1),
  workspaceId: z.string().min(1),
  accountId: z.string().min(1),
  folderKey: z.string().min(1),
  providerId: mailProviderIdSchema,
  strategy: mailWatchStrategySchema,
  identity: mailSignalIdentitySchema,
  occurredAt: z.string().datetime(),
});
export type MailChangeSignalV1 = z.infer<typeof mailChangeSignalV1Schema>;

export const mailChangedEventV1Schema = z
  .object({
    schemaVersion: z.literal(1),
    eventId: z.string().uuid(),
    workspaceId: z.string().min(1),
    accountId: z.string().min(1),
    folderIds: z.array(z.string().min(1)).max(1_000),
    invalidation: z.literal('folders'),
    addedCount: z.number().int().nonnegative(),
    updatedCount: z.number().int().nonnegative(),
    deletedCount: z.number().int().nonnegative(),
    effectiveStatus: z.string().min(1),
    watchStatus: mailWatchStatusSchema,
    committedAt: z.string().datetime(),
  })
  .strict();
export type MailChangedEventV1 = z.infer<typeof mailChangedEventV1Schema>;

export const mailDeliveryRequestedV1Schema = z
  .object({
    schemaVersion: z.literal(1),
    receiptId: z.string().min(1),
    eventId: z.string().uuid(),
    workspaceId: z.string().min(1),
    accountId: z.string().min(1),
    userId: z.string().min(1),
    deviceId: z.string().min(1),
  })
  .strict();
export type MailDeliveryRequestedV1 = z.infer<
  typeof mailDeliveryRequestedV1Schema
>;

export const MAIL_PUSH_CONTRACT = Object.freeze({
  SW_DEDUPE_TTL_DAYS: 92,
  SW_DEDUPE_MAX_IDS: 250_000,
  SW_DEDUPE_MAX_BYTES: 33_554_432,
  RETRY_MAX_RECORDS: 100,
  RETRY_MAX_BYTES: 1_048_576,
  PUSH_TTL_SECONDS: 300,
} as const);

export const MAIL_REALTIME_RETENTION = Object.freeze({
  SIGNAL_RECEIPT_DAYS: 8,
  PROCESSED_OUTBOX_DAYS: 32,
  DELIVERY_RECEIPT_DAYS: 92,
  DEAD_LETTER_REDRIVE_DAYS: 90,
  DEAD_LETTER_TOMBSTONE_DAYS: 365,
  MAX_OUTBOX_PAYLOAD_BYTES: 1_048_576,
} as const);

export const mailPushPayloadV1Schema = z
  .object({
    schemaVersion: z.literal(1),
    eventId: z.string().uuid(),
    workspaceId: z.string().min(1),
    accountId: z.string().min(1),
    route: z.literal('mail'),
  })
  .strict();
export type MailPushPayloadV1 = z.infer<typeof mailPushPayloadV1Schema>;

const localTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);
export const mailQuietHoursV1Schema = z
  .object({
    schemaVersion: z.literal(1),
    timezone: z.string().min(1),
    start: localTimeSchema,
    end: localTimeSchema,
    days: z.array(z.number().int().min(1).max(7)).min(1).max(7),
  })
  .strict();
export type MailQuietHoursV1 = z.infer<typeof mailQuietHoursV1Schema>;

const endpointPresetSchema = z.object({
  domains: z.array(z.string().min(1)).readonly(),
  imapHost: z.string().min(1),
  imapPort: z.number().int().min(1).max(65535),
  imapTls: z.boolean(),
  smtpHost: z.string().min(1),
  smtpPort: z.number().int().min(1).max(65535),
  smtpTls: z.boolean(),
});

export const mailProviderProfileSchema = z.object({
  id: mailProviderIdSchema,
  providerKind: mailProviderKindSchema,
  name: z.string().min(1),
  hint: z.string().min(1),
  primaryStrategy: mailWatchStrategySchema,
  availability: z.enum(['available', 'coming_soon']),
  supportsImapIdle: z.boolean(),
  supportsCondstore: z.literal('negotiated'),
  supportsQresync: z.literal('negotiated'),
  supportsJmapPush: z.literal('discovered'),
  endpoints: endpointPresetSchema.optional(),
});
export type MailProviderProfile = z.infer<typeof mailProviderProfileSchema>;

const genericProfile = (
  profile: Omit<
    MailProviderProfile,
    | 'providerKind'
    | 'primaryStrategy'
    | 'availability'
    | 'supportsImapIdle'
    | 'supportsCondstore'
    | 'supportsQresync'
    | 'supportsJmapPush'
  >
): MailProviderProfile => ({
  ...profile,
  providerKind: 'generic_imap_smtp',
  primaryStrategy: 'imap_idle',
  availability: 'available',
  supportsImapIdle: true,
  supportsCondstore: 'negotiated',
  supportsQresync: 'negotiated',
  supportsJmapPush: 'discovered',
});

export const MAIL_PROVIDER_CATALOG = mailProviderProfileSchema.array().parse([
  {
    id: 'gmail_oauth',
    providerKind: 'gmail_oauth',
    name: 'Gmail',
    hint: 'Google Workspace / Gmail',
    primaryStrategy: 'gmail_pubsub',
    availability: 'available',
    supportsImapIdle: true,
    supportsCondstore: 'negotiated',
    supportsQresync: 'negotiated',
    supportsJmapPush: 'discovered',
  },
  {
    id: 'microsoft_oauth',
    providerKind: 'microsoft_oauth',
    name: 'Microsoft',
    hint: 'Microsoft 365 / Outlook',
    primaryStrategy: 'microsoft_graph',
    availability: 'available',
    supportsImapIdle: true,
    supportsCondstore: 'negotiated',
    supportsQresync: 'negotiated',
    supportsJmapPush: 'discovered',
  },
  genericProfile({
    id: 'gmx',
    name: 'GMX',
    hint: 'GMX Mail',
    endpoints: {
      domains: ['gmx.de', 'gmx.net', 'gmx.com', 'gmx.at', 'gmx.ch'],
      imapHost: 'imap.gmx.net',
      imapPort: 993,
      imapTls: true,
      smtpHost: 'mail.gmx.net',
      smtpPort: 587,
      smtpTls: false,
    },
  }),
  genericProfile({
    id: 'web_de',
    name: 'WEB.DE',
    hint: 'WEB.DE Mail',
    endpoints: {
      domains: ['web.de'],
      imapHost: 'imap.web.de',
      imapPort: 993,
      imapTls: true,
      smtpHost: 'smtp.web.de',
      smtpPort: 587,
      smtpTls: false,
    },
  }),
  genericProfile({
    id: 'mailbox_org',
    name: 'mailbox.org',
    hint: 'mailbox.org',
    endpoints: {
      domains: ['mailbox.org'],
      imapHost: 'imap.mailbox.org',
      imapPort: 993,
      imapTls: true,
      smtpHost: 'smtp.mailbox.org',
      smtpPort: 465,
      smtpTls: true,
    },
  }),
  genericProfile({
    id: 'posteo',
    name: 'Posteo',
    hint: 'posteo.de',
    endpoints: {
      domains: ['posteo.de', 'posteo.net', 'posteo.org'],
      imapHost: 'posteo.de',
      imapPort: 993,
      imapTls: true,
      smtpHost: 'posteo.de',
      smtpPort: 587,
      smtpTls: false,
    },
  }),
  genericProfile({
    id: 'ionos',
    name: 'IONOS',
    hint: 'IONOS Mail',
    endpoints: {
      domains: ['ionos.de', 'ionos.com'],
      imapHost: 'imap.ionos.com',
      imapPort: 993,
      imapTls: true,
      smtpHost: 'smtp.ionos.com',
      smtpPort: 465,
      smtpTls: true,
    },
  }),
  genericProfile({
    id: 'mail_de',
    name: 'mail.de',
    hint: 'mail.de',
    endpoints: {
      domains: ['mail.de'],
      imapHost: 'imap.mail.de',
      imapPort: 993,
      imapTls: true,
      smtpHost: 'smtp.mail.de',
      smtpPort: 587,
      smtpTls: false,
    },
  }),
  genericProfile({
    id: 'freenet',
    name: 'freenet',
    hint: 'freenet Mail',
    endpoints: {
      domains: ['freenet.de'],
      imapHost: 'mx.freenet.de',
      imapPort: 993,
      imapTls: true,
      smtpHost: 'mx.freenet.de',
      smtpPort: 465,
      smtpTls: true,
    },
  }),
  genericProfile({
    id: 'strato',
    name: 'STRATO',
    hint: 'STRATO Mail',
    endpoints: {
      domains: ['strato.de'],
      imapHost: 'imap.strato.de',
      imapPort: 993,
      imapTls: true,
      smtpHost: 'smtp.strato.de',
      smtpPort: 465,
      smtpTls: true,
    },
  }),
  genericProfile({
    id: 'generic_imap_smtp',
    name: 'Other provider',
    hint: 'IMAP / SMTP',
  }),
  {
    ...genericProfile({
      id: 'custom_oauth2',
      name: 'Custom OAuth 2.0',
      hint: 'OAuth 2.0 + IMAP / SMTP',
    }),
    providerKind: 'custom_oauth2',
  },
]);

export function getMailProviderProfile(
  id: MailProviderId
): MailProviderProfile {
  const profile = MAIL_PROVIDER_CATALOG.find(profile => profile.id === id);
  if (!profile) throw new Error(`Unknown mail provider: ${id}`);
  return profile;
}
